import { css } from '@emotion/react'
import { graphql, useStaticQuery } from 'gatsby'
import { throttle } from 'lodash'
import {
  type ComponentProps,
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { Seal } from '@/features/brearley-logo'
import { DatoStructuredText, ScrollToggle } from '@/features/common'
import { DatoLink } from '@/features/dato-link'
import { useEscKeyFunction } from '@/hooks/useEscKeyFunction'
import { absoluteFill } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { useClassNotesContext } from '../../context/classNotesContext'
import { ClassNotesMilestonesSection } from './ClassNotesMilestonesSection'
import { ClassNotesYearList } from './ClassNotesYearList'

type Props = ComponentProps<'div'> & {
  classYear?: number | 'Milestones' | 'Faculty/Staff Notes'
  allYearsWithData?: { year: number; firstSlug: string }[]
  allMilestones?: { heading: string; slug: string }[]
  allFacultyStaffNotes?: { heading: string; slug: string }[]
  initialScrollPos?: number
  isLanding: boolean
}

export const CNNExpanded = ({
  classYear,
  allYearsWithData,
  allMilestones,
  allFacultyStaffNotes,
  initialScrollPos,
  isLanding,
  ...props
}: Props) => {
  const { datoCmsClassNotesPage: data } =
    useStaticQuery<Queries.CNNExpandedQuery>(graphql`
      query CNNExpanded {
        datoCmsClassNotesPage {
          heading
          body {
            value
          }
          milestonesHeading
          milestonesBody {
            value
          }
          facultyStaffHeading
          facultyStaffBody {
            value
          }
          # milestonesThumbnail {
          #   gatsbyImageData(
          #     imgixParams: {
          #       fit: "crop"
          #       crop: "focalpoint"
          #       ar: "4:3"
          #       q: 50
          #     }
          #     width: 480
          #   )
          #   ...ImageFocalData
          # }
          cta {
            ... on DatoCmsFormLink {
              ...FormLink
            }
          }
        }
      }
    `)

  const ref = useRef<HTMLDivElement | null>(null)
  const [scrollPos, setScrollPos] = useState(initialScrollPos || 0)
  useEffect(() => {
    ref.current?.scrollTo({ top: initialScrollPos })
  }, [initialScrollPos])
  useEffect(() => {
    const container = ref.current
    const handleScroll = throttle(() => {
      setScrollPos(container?.scrollTop || 0)
    }, 50)
    container?.addEventListener('scroll', handleScroll, {
      passive: true,
    })
    return () => {
      container?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const { isExpanded, setExpanded } = useClassNotesContext()
  useLayoutEffect(() => {
    if (isLanding) {
      setExpanded(true)
    }
  }, [isLanding, setExpanded])
  useEscKeyFunction(() => !isLanding && setExpanded(false))

  const styles = {
    background: css`
      position: fixed;
      z-index: 3;
      pointer-events: none;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      background-color: #000;
      transition: opacity 500ms ease 100ms;
      ${isExpanded &&
      css`
        opacity: 1;
        transition-delay: 0ms;
      `}
    `,
    expandedNav: css`
      position: fixed;
      z-index: 5;
      width: 100%;
      top: var(--nav-height-min);
      left: 0;
      transition: height 500ms ease;
      background-color: ${colors.gray10};
      display: grid;
      grid-template-rows: auto 1fr;
      align-content: flex-start;
      overflow: auto;

      ${!isExpanded &&
      css`
        height: 0px;
      `}
      ${isExpanded &&
      css`
        height: calc(100% - var(--nav-height-min));
      `}
    `,
    hero: css`
      position: relative;
      display: grid;
      text-align: center;
      color: #fff;
      background-color: ${colors.gray10};
      justify-items: center;
      padding: var(--row-36) var(--margin);
    `,
    heading: css`
      font-size: var(--fs-48);
      margin: 0;
    `,
    body: css`
      max-width: 85ch;
    `,
    button: css`
      margin: 1em 0 0;
    `,
    sealContainer: css`
      ${absoluteFill}
      overflow: hidden;
    `,
    seal: css`
      position: absolute;
      width: 37.5vw;
      right: -5vw;
    `,
    yearListContainer: css`
      background-color: ${colors.gray98};
    `,
    close: css`
      position: absolute;
      top: 0.75rem;
      right: var(--margin);
      width: 2.5rem;
      height: 2.5rem;
      color: hsla(0, 0%, 100%, 0.75);
      &:before,
      &:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 2px;
        top: 50%;
        left: 50%;
        background: currentColor;
      }
      &:before {
        transform: translate(-50%, -50%) rotate(-45deg);
      }
      &:after {
        transform: translate(-50%, -50%) rotate(45deg);
      }
      @media (hover: hover) {
        &:hover {
          color: ${colors.redLight};
        }
      }
      ${isLanding &&
      css`
        display: none;
      `}
    `,
  }
  return (
    <Fragment>
      <div css={styles.background} />
      <div
        css={styles.expandedNav}
        ref={ref}
        {...props}
      >
        {isExpanded && <ScrollToggle />}
        <div css={styles.hero}>
          <div css={styles.sealContainer}>
            <Seal
              css={styles.seal}
              color={'rgba(255,255,255,0.1)'}
            />
          </div>
          <h2 css={styles.heading}>{data?.heading}</h2>
          <div css={styles.body}>
            <DatoStructuredText data={data?.body} />
          </div>
          <DatoLink
            css={styles.button}
            showIcon
            styleVariant={'BUTTON'}
            data={data?.cta}
          />
          <button
            css={styles.close}
            onClick={() => setExpanded(false)}
          />
        </div>
        <div css={styles.yearListContainer}>
          <ClassNotesYearList
            scrollPos={scrollPos}
            classYear={classYear}
            allYearsWithData={allYearsWithData}
          />
          <ClassNotesMilestonesSection
            data={data}
            allMilestones={allMilestones}
            allFacultyStaffNotes={allFacultyStaffNotes}
          />
        </div>
      </div>
    </Fragment>
  )
}
